import { Component, EventEmitter, Input, Output } from '@angular/core';

import { TwcProgressBarType } from '@topcon/twc-angular';

import { PcvizUploadConfig, PcvizUploadConfigFileInfo } from '@mirage-projects/cw-models';
import { DateTimeHelperService, FilesHelperService } from '@mirage-projects/cw-utils';

/**
 * Component to upload files
 */
@Component({
    selector: 'cw-upload-list',
    templateUrl: './upload-list.component.html',
    styleUrls: ['./upload-list.component.scss'],
    standalone: false,
})
export class UploadListComponent {
    /** Files to be listed */
    @Input() files: File[] = [];
    /** Information on the status of the entire process and of each file: progress, remaining time... */
    @Input() uploadInfo: PcvizUploadConfig | null = null;
    /** Shows/hides the control buttons(Cancel, pause and resume) */
    @Input() showUploadControlButtons = true;

    @Output() cancelUpload = new EventEmitter();
    @Output() pauseUpload = new EventEmitter();
    @Output() resumeUpload = new EventEmitter();

    constructor(
        private filesHelperService: FilesHelperService,
        private dateTimeHelperService: DateTimeHelperService
    ) {}

    /**
     * @returns Percentage of progress of the complete process
     * @ignore
     */
    getProgressProcess(): number {
        return this.uploadInfo?.upload?.progress ? this.uploadInfo.upload.progress : 0;
    }

    /**
     * @returns progress bar type of the complete process
     * @ignore
     */
    getTypeProcess(): TwcProgressBarType {
        switch (this.uploadInfo?.upload?.status) {
            case 'SUCCESS':
                return 'success';
            case 'FAILURE':
                return 'error';
            default:
                return 'primary-info';
        }
    }

    /**
     * @returns Label of the complete process
     * @ignore
     */
    getLabelProcess(): string {
        if (this.uploadInfo?.upload?.status === 'SELECTED') {
            return 'JOBS.STARTING';
        }

        return this.uploadInfo?.upload?.status ? `JOBS.${this.uploadInfo?.upload?.status}` : '';
    }

    /** @ignore */
    getRemaining(): string {
        let remaining = '';
        if (this.isLoading()) {
            remaining = ` (${this.dateTimeHelperService.millisecondsToHHMMSS(this.uploadInfo?.upload?.remainingTime)})`;
        }
        return remaining;
    }

    /** @ignore */
    isPaused(): boolean {
        return this.uploadInfo?.upload?.status === 'PAUSED';
    }

    /** @ignore */
    isCanceled(): boolean {
        return this.uploadInfo?.upload?.status === 'CANCELLED';
    }

    /** @ignore */
    isLoading(): boolean {
        return ['UPLOADING'].includes(this.uploadInfo?.upload?.status as string);
    }

    /**
     * @returns Percentage of progress of the input file
     * @ignore
     */
    getProgress(file: File): number {
        const infoFile = this.getFileInfo(file);

        return infoFile?.progress ? infoFile.progress : 0;
    }

    /**
     * @returns Progress bar type of the input file
     * @ignore
     */
    getType(file: File): TwcProgressBarType {
        const infoFile = this.getFileInfo(file);

        return infoFile && infoFile.completed ? 'success' : 'primary-info';
    }

    /**
     * @returns Label of the input file
     * @ignore
     */
    getLabel(file: File): string {
        const infoFile = this.getFileInfo(file);
        if (infoFile && infoFile.completed) {
            return 'JOBS.SUCCESS';
        } else {
            if (!infoFile || infoFile.progress === 0) {
                return 'JOBS.STARTING';
            } else {
                return 'JOBS.UPLOADING';
            }
        }
    }

    /** @ignore */
    getFileInfo(file: File): PcvizUploadConfigFileInfo | null {
        const areThereFiles = this.uploadInfo?.upload?.filesInfo;
        if (!areThereFiles) {
            return null;
        }

        const res = areThereFiles.find((infoFile: PcvizUploadConfigFileInfo) => infoFile.name === file.name && infoFile.size === file.size);
        return res ? res : null;
    }

    /** @ignore */
    clickCancel(): void {
        if (typeof this.uploadInfo?.upload?.cancel === 'function') {
            this.uploadInfo.upload.cancel();
        }
        this.cancelUpload.emit();
    }

    /** @ignore */
    clickPause(): void {
        if (typeof this.uploadInfo?.upload?.pause === 'function') {
            this.uploadInfo.upload.pause();
        }
        this.pauseUpload.emit();
    }

    /** @ignore */
    clickContinue(): void {
        if (typeof this.uploadInfo?.upload?.resume === 'function') {
            this.uploadInfo.upload.resume();
        }
        this.resumeUpload.emit();
    }

    /** @ignore */
    getExtension(file: File): string {
        return this.filesHelperService.getExtension(file);
    }

    /** @ignore */
    get uploadRemaningTime(): number | undefined {
        if (!this.isLoading()) {
            return;
        }
        return this.uploadInfo?.upload?.remainingTime;
    }
}
